import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { runMultiMatches } from '../../utils/api';
import centralDiscoveryQueue from '../../utils/centralDiscoveryQueue';

/**
 * Distribution Bar Chart Template
 * Based on Figma design: https://www.figma.com/design/Nhga9ODmaPGwSQJjO8lQZi/Calibrate-Mate?node-id=300-14413&m=dev
 * Example: Company Stage Discovery
 * 
 * Design specs from Figma:
 * - Chat bubble with 10px border radius (10px 10px 10px 0px)
 * - Card with 12px border radius, stroke rgba(192, 197, 205, 0.5)
 * - Card header with bottom border #EAECF0
 * - Bars with #1C8FFD background, 8px border radius
 * - Title: 500 weight, 16px size
 * - Subtitle: 400 weight, 12px size, #667085 color
 */
const DistributionBarChartTemplate = ({
  title = "I reviewed your existing workforce, and this is the distribution across different company stages. Which company stage(s) would you like to target?",
  chartTitle,
  chartSubtitle,
  data = [],
  selectedItems = [],
  onConfirmed,
  confirmButtonText = "Select at Least One Option",
  readOnly = false,
  showSkipButton = false,
  onSkipDiscovery,
  skipButtonText = "Skip Discovery",
  hide_zero_percentage = false, // Option to hide items with 0% percentage
  allowEmptySelection = false // Allow confirming with no selections (will remove queue item)
}) => {
  // Initialize with query IDs if available, or pre-select highest percentage items if none specified
  const [localSelectedItems, setLocalSelectedItems] = useState(() => {
    if (selectedItems.length > 0) {
      return selectedItems.map(item => {
        if (typeof item === 'object') {
          return item.queryId || item.originalValue || item.value || item;
        }
        return item;
      });
    } else {
      // Pre-select only the highest percentage items by default (top 50% or at least 1)
      const sortedData = [...data].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
      const totalPercentage = sortedData.reduce((sum, item) => sum + (item.percentage || 0), 0);
      const threshold = totalPercentage * 0.5; // Select items that make up top 50%
      
      let cumulativePercentage = 0;
      const topItems = [];
      
      for (const item of sortedData) {
        if (topItems.length === 0 || cumulativePercentage < threshold) {
          topItems.push(item.queryId || item.name);
          cumulativePercentage += (item.percentage || 0);
        } else {
          break;
        }
      }
      
      // Ensure at least one item is selected
      if (topItems.length === 0 && data.length > 0) {
        topItems.push(data[0].queryId || data[0].name);
      }
      
      return topItems;
    }
  });
  const [chartData, setChartData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);

  const sortedByPercentage = useMemo(() => {
    const arr = [...chartData];
    arr.sort((a, b) => (b.percentage || 0) - (a.percentage || 0) || ((a.name || '').localeCompare(b.name || '')));
    return arr;
  }, [chartData]);


  // Calculate real percentages using runMultiMatches
  useEffect(() => {
    const calculateRealPercentages = async () => {
      if (!data || data.length === 0) return;
      
      setIsLoading(true);
      
      try {
        // Get all completed queue items (like handleRefreshResultsFromQueue does)
        const allQueueItems = centralDiscoveryQueue.getAllItems();
        const completedItems = allQueueItems.filter(item => item.disco_done === true);
        
        if (completedItems.length === 0) {
          console.log('📊 DistributionBarChart: No completed queue items found, using default percentages');
          setChartData(data);
          setIsLoading(false);
          return;
        }

        // Construct ICP requirements from completed queue items (same as handleRefreshResultsFromQueue)
        const icpRequirements = completedItems.map(item => ({
          section_type: item.section_type || 'Must Have',
          section_name: item.section_name,
          section_id: item.section_id,
          bool_str: item.bool_str || ''
        }));

        console.log('📊 DistributionBarChart: Using ICP requirements:', icpRequirements.length);

        // Create to_match array from each query ID in the data

        const toMatch = data.map(item => item.queryId);
        
        console.log('📊 DistributionBarChart: Calling runMultiMatches with:', {
          requirements: icpRequirements.length,
          toMatch: toMatch.length,
          run_on_self_org: true
        });

        // Call runMultiMatches with run_on_self_org: true
        const multiMatchResults = await runMultiMatches(
          icpRequirements,
          toMatch,
          [], // to_match_requirements (empty)
          { run_on_self_org: true }
        );

        console.log('📊 DistributionBarChart: runMultiMatches results:', multiMatchResults);

        if (multiMatchResults) {
          // Extract count_by_queries from the response
          const countByQueries = multiMatchResults.count_by_queries || multiMatchResults;
          
          // Calculate total count
          const totalCount = Object.values(countByQueries).reduce((sum, count) => sum + (count || 0), 0);
          
          console.log('📊 DistributionBarChart: Count by queries:', countByQueries);
          console.log('📊 DistributionBarChart: Total count:', totalCount);
          
          // Calculate percentages for each item
          const updatedData = data.map(item => {
            const count = countByQueries[item.queryId] || 0;
            const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
            
            console.log(`📊 DistributionBarChart: ${item.name} (${item.queryId}): count=${count}, percentage=${percentage}%`);
            
            return {
              ...item,
              percentage,
              count
            };
          });

          console.log('📊 DistributionBarChart: Updated data with percentages:', updatedData);
          setChartData(updatedData);
        } else {
          console.warn('📊 DistributionBarChart: No results from runMultiMatches, using default data');
          setChartData(data);
        }
        
      } catch (error) {
        console.error('📊 DistributionBarChart: Error calculating percentages:', error);
        setChartData(data); // Fallback to original data
      } finally {
        setIsLoading(false);
      }
    };

    calculateRealPercentages();
  }, [data]);

  // Update selected items when data changes to pre-select highest percentage items
  useEffect(() => {
    if (selectedItems.length === 0 && chartData.length > 0) {
      // Pre-select only the highest percentage items by default (top 50% or at least 1)
      const sortedData = [...chartData].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
      const totalPercentage = sortedData.reduce((sum, item) => sum + (item.percentage || 0), 0);
      const threshold = totalPercentage * 0.5; // Select items that make up top 50%
      
      let cumulativePercentage = 0;
      const topItems = [];
      
      for (const item of sortedData) {
        if (topItems.length === 0 || cumulativePercentage < threshold) {
          topItems.push(item.queryId || item.name);
          cumulativePercentage += (item.percentage || 0);
        } else {
          break;
        }
      }
      
      // Ensure at least one item is selected
      if (topItems.length === 0 && chartData.length > 0) {
        topItems.push(chartData[0].queryId || chartData[0].name);
      }
      
      setLocalSelectedItems(topItems);
    }
  }, [chartData, selectedItems]);

  const handleBarClick = (item) => {
    if (readOnly) return;
    
    setLocalSelectedItems(prev => {
      const queryId = item.queryId || item.name; // Use queryId if available, fallback to name
      if (prev.includes(queryId)) {
        return prev.filter(i => i !== queryId);
      } else {
        return [...prev, queryId];
      }
    });
  };

  const handleConfirm = () => {
    if (onConfirmed) {
      // If no items selected and empty selection is allowed, signal to remove queue item
      if (localSelectedItems.length === 0 && allowEmptySelection) {
        onConfirmed({ removeQueueItem: true, selections: [] });
        return;
      }
      
      // Convert selected items to proper format with both queryId and name
      const selections = localSelectedItems.map(selectedId => {
        const item = chartData.find(d => (d.queryId || d.name) === selectedId);
        return {
          queryId: item?.queryId || selectedId,
          label: item?.name || selectedId,
          originalValue: item?.queryId || selectedId
        };
      });
      
      // For selections array, use queryIds if available, otherwise use names
      const selectionsArray = localSelectedItems.map(selectedId => {
        const item = chartData.find(d => (d.queryId || d.name) === selectedId);
        return item?.queryId || selectedId; // Use queryId if available, fallback to selectedId
      });
      
      onConfirmed({ 
        selections: selectionsArray, // Array of queryIds (for taxonomy items) or names (for regular items)
        template_result: selections  // Detailed objects with queryId, label, originalValue
      });
    }
  };

  // Calculate max value for bar scaling
  const maxValue = Math.max(...chartData.map(item => item.percentage || 0));

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        padding: '0px 8px',
        backgroundColor: 'transparent',
      }}
    >
      {/* Chat Bubble Container */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px 10px 10px 0px',
          padding: '0px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* Title Text */}
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '1.375em',
            color: '#101828',
            textAlign: 'left',
          }}
        >
          {title}
        </Typography>

        {/* Chart Card */}
        <Box
          sx={{
            border: '1px solid rgba(192, 197, 205, 0.5)', // Exact Figma color
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            opacity: readOnly ? 0.5 : 1, // Semi-transparent in read-only mode as per Figma
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              borderBottom: '1px solid #EAECF0', // Exact Figma color
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '2px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '16px',
                    fontWeight: 500, // Exact Figma weight
                    lineHeight: '1.171875em',
                    color: '#101828',
                  }}
                >
                  {chartTitle}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '1.1999999682108562em',
                    color: '#667085', // Exact Figma color
                  }}
                >
                  {chartSubtitle}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chart Content */}
          <Box
            sx={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            {/* Loading State */}
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  minHeight: '120px',
                }}
              >
                <CircularProgress 
                  size={24} 
                  sx={{ color: '#4599FA' }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#667085',
                    textAlign: 'center',
                  }}
                >
                  Analyzing your workforce distribution...
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    alignSelf: 'stretch',
                    maxHeight: '280px',
                    overflowY: 'auto',
                    pr: '4px'
                  }}
                >
                  {(readOnly
                    ? chartData.filter(item => {
                        // Read-only mode: optionally hide 0% rows
                        if (hide_zero_percentage && (item.percentage <= 1 || item.percentage === undefined)) {
                          return false;
                        }
                        // In read-only mode, only show items that were selected by the user
                        const itemId = item.queryId || item.name;
                        return localSelectedItems.includes(itemId);
                      })
                    : sortedByPercentage
                  ).map((item, index) => {
                  const isSelected = localSelectedItems.includes(item.queryId || item.name);
                  const pct = Number.isFinite(item.percentage) ? item.percentage : 0;
                  const widthPercent = maxValue > 0 ? Math.max((pct / maxValue) * 100, 5) : 5; // Always minimum 5% and avoid NaN
                  
                  return (
                    <Box
                      key={index}
                      onClick={() => handleBarClick(item)}
                      sx={{
                        display: 'flex',
                        cursor: readOnly ? 'default' : 'pointer',
                        opacity: readOnly ? 1 : (isSelected ? 1 : 0.7),
                        '&:hover': readOnly ? {} : {
                          opacity: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          width: '494px', // Fixed width from Figma
                        }}
                      >
                        {/* Label Row */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              flex: 1,
                            }}
                          >
                            {/* Check Circle Icon for selected items in read-only mode */}
                            {readOnly && isSelected && (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: '16px',
                                  color: '#1C8FFD',
                                }}
                              />
                            )}
                            
                            <Typography
                              sx={{
                                fontFamily: 'Roboto',
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '1.4285714285714286em',
                                color: '#374151',
                                textAlign: 'left',
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={decodeURIComponent(item.name)} // Tooltip for full text
                            >
                              {decodeURIComponent(item.name)}
                            </Typography>
                          </Box>
                          
                          {/* Percentage */}
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '1.3846153846153846em',
                              color: '#465366', // Exact Figma color
                              textAlign: 'center',
                              minWidth: '40px',
                            }}
                          >
                            {item.percentage}%
                          </Typography>
                        </Box>
                        
                        {/* Bar Row */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '32px', // Original bar height
                            backgroundColor: '#E5E7EB', // Background track
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              backgroundColor: readOnly || isSelected ? '#1C8FFD' : '#BDD7F6',
                              borderRadius: '8px',
                              width: `${widthPercent}%`, // Minimum 5% for visibility, safe against NaN
                              transition: 'width 0.3s ease, background-color 0.3s ease',
                              border: readOnly || isSelected ? '2px solid #0F42BC' : 'none',
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Buttons Row - Confirm and Skip side by side */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
          {readOnly ? (
            /* Read-only "Confirmed" label - exact Figma styling */
            <Box
              sx={{
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '1.4285714285714286em',
                bgcolor: '#FFFFFF',
                color: '#969DAD', // Exact Figma color
                border: '1px solid #EAECF0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '36px',
                fontFamily: 'Roboto',
              }}
            >
              Confirmed
            </Box>
          ) : (
            <>
              {/* Interactive confirm button */}
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={localSelectedItems.length === 0 && !allowEmptySelection}
                sx={{
                  backgroundColor: (localSelectedItems.length === 0 && !allowEmptySelection) ? '#BDD7F6' : '#4599FA', // Figma disabled color
                  color: '#FFFFFF',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  fontWeight: 400,
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  height: '36px',
                  '&:hover': {
                    backgroundColor: (localSelectedItems.length === 0 && !allowEmptySelection) ? '#BDD7F6' : '#3b82d6',
                  },
                  '&:disabled': {
                    backgroundColor: '#BDD7F6',
                    color: '#FFFFFF',
                  }
                }}
              >
                {localSelectedItems.length === 0 && allowEmptySelection ? 'Skip This Discovery' : confirmButtonText}
              </Button>
              
              {/* Skip Button */}
              {showSkipButton && (
                <Button
                  variant="outlined"
                  onClick={onSkipDiscovery}
                  sx={{
                    color: '#667085',
                    borderColor: '#D0D5DD',
                    backgroundColor: '#F9FAFB',
                    fontWeight: 400,
                    fontSize: '14px',
                    padding: '8px 16px',
                    height: '36px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#F2F4F7',
                      borderColor: '#B8BCC8',
                    }
                  }}
                >
                  {skipButtonText}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DistributionBarChartTemplate;
